import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auth
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
export const exchangeSession = (session_id) =>
  api.post("/auth/session", { session_id }).then((r) => r.data);
export const logout = () => api.post("/auth/logout").then((r) => r.data);
export const completeWizard = () => api.post("/auth/complete-wizard").then((r) => r.data);

// Children
export const listChildren = () => api.get("/children").then((r) => r.data);
export const createChild = (data) => api.post("/children", data).then((r) => r.data);
export const updateChild = (id, data) => api.patch(`/children/${id}`, data).then((r) => r.data);
export const deleteChild = (id) => api.delete(`/children/${id}`).then((r) => r.data);
export const switchChild = (child_id, pin) =>
  api.post("/children/switch", { child_id, pin }).then((r) => r.data);

// Curriculum
export const listCurriculumBoards = () => api.get("/curriculum/boards").then((r) => r.data);
export const listCurriculumSubjects = () => api.get("/curriculum/subjects").then((r) => r.data);
export const listTopics = (subject) =>
  api.get("/curriculum/topics", { params: subject ? { subject } : {} }).then((r) => r.data);
export const getTopic = (id) => api.get(`/curriculum/topics/${id}`).then((r) => r.data);
export const getTopicQuiz = (id) => api.get(`/curriculum/topics/${id}/quiz`).then((r) => r.data);
export const submitQuiz = (topic_id, answers) =>
  api.post("/quiz/submit", { topic_id, answers }).then((r) => r.data);
export const generateTopicPodcast = (topic_id, style, tone) =>
  api.post(`/curriculum/topics/${topic_id}/podcast`, { style, tone }).then((r) => r.data);
export const listTopicPodcasts = (topic_id) =>
  api.get(`/curriculum/topics/${topic_id}/podcasts`).then((r) => r.data);
export const curriculumPodcastUrl = (id) => `${API}/curriculum/podcasts/${id}/audio`;

// Progress
export const startSession = (topic_id, activity = "book") =>
  api.post("/progress/start", { topic_id, activity }).then((r) => r.data);
export const endSession = (session_id) =>
  api.post("/progress/end", { session_id }).then((r) => r.data);
export const parentDashboard = () => api.get("/parent/dashboard").then((r) => r.data);

// Payments
export const listPlans = () => api.get("/payments/plans").then((r) => r.data);
export const startCheckout = (lookup_key) =>
  api
    .post("/payments/checkout", { lookup_key, origin_url: window.location.origin })
    .then((r) => r.data);
export const getPaymentStatus = (session_id) =>
  api.get(`/payments/status/${session_id}`).then((r) => r.data);
export const openBillingPortal = (return_url) =>
  api.post("/payments/portal", { return_url }).then((r) => r.data);

// Mock Exam
export const startMockExam = (subject, opts = {}) =>
  api.post("/mock-exam/start", { subject, ...opts }).then((r) => r.data);
export const submitMockExam = (exam_id, answers) =>
  api.post("/mock-exam/submit", { exam_id, answers }).then((r) => r.data);
export const getMockExam = (exam_id) =>
  api.get(`/mock-exam/${exam_id}`).then((r) => r.data);
export const mockExamHistory = (subject) =>
  api.get("/mock-exam/history/list", { params: subject ? { subject } : {} }).then((r) => r.data);
export const parentExamHistory = () =>
  api.get("/parent/mock-exam/history").then((r) => r.data);

// Leaderboards
export const familyLeaderboard = () => api.get("/leaderboard/family").then((r) => r.data);
export const listLeagues = () => api.get("/leagues").then((r) => r.data);
export const createLeague = (name) => api.post("/leagues", { name }).then((r) => r.data);
export const joinLeague = (code) => api.post("/leagues/join", { code }).then((r) => r.data);
export const leagueLeaderboard = (id) => api.get(`/leagues/${id}/leaderboard`).then((r) => r.data);
export const leaveLeague = (id) => api.post(`/leagues/${id}/leave`).then((r) => r.data);

// Referrals
export const getMyReferrals = () => api.get("/referrals/me").then((r) => r.data);
export const applyReferralCode = (code) => api.post("/referrals/apply", { code }).then((r) => r.data);

// Refunds
export const requestRefund = (reason, details) =>
  api.post("/refunds/request", { reason, details }).then((r) => r.data);
export const myRefunds = () => api.get("/refunds/my").then((r) => r.data);

// Verified Diagrams — public
export const listTopicDiagrams = (topic_id) =>
  api.get(`/curriculum/topics/${topic_id}/diagrams`).then((r) => r.data);
export const diagramImageUrl = (id) => `${API}/curriculum/diagrams/${id}/image`;
export const diagramImageUrlAdmin = (id, key) =>
  `${API}/curriculum/diagrams/${id}/image?admin=${encodeURIComponent(key)}`;

// Verified Diagrams — admin (requires X-Reconcile-Key header)
export const adminListTopics = (key) =>
  api.get("/admin/topics", { headers: { "X-Reconcile-Key": key } }).then((r) => r.data);
export const adminListTopicDiagrams = (topic_id, key) =>
  api.get(`/admin/topics/${topic_id}/diagrams`, { headers: { "X-Reconcile-Key": key } }).then((r) => r.data);
export const adminUploadDiagram = (topic_id, key, file, title, caption) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("title", title);
  if (caption) fd.append("caption", caption);
  return api
    .post(`/admin/topics/${topic_id}/diagrams`, fd, {
      headers: { "X-Reconcile-Key": key, "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};
export const adminDeleteDiagram = (id, key) =>
  api.delete(`/admin/diagrams/${id}`, { headers: { "X-Reconcile-Key": key } }).then((r) => r.data);
export const adminPatchDiagram = (id, key, data) =>
  api.patch(`/admin/diagrams/${id}`, data, { headers: { "X-Reconcile-Key": key } }).then((r) => r.data);

// Admin Console
const adminHdrs = (key) => ({ headers: { "X-Reconcile-Key": key } });
export const adminStats = (key) => api.get("/admin/stats", adminHdrs(key)).then((r) => r.data);
export const adminWaitlist = (key, params) => api.get("/admin/waitlist", { ...adminHdrs(key), params }).then((r) => r.data);
export const adminWaitlistCsvUrl = (key) => `${API}/admin/waitlist/export.csv?admin=${encodeURIComponent(key)}`;
export const adminWaitlistDelete = (id, key) => api.delete(`/admin/waitlist/${id}`, adminHdrs(key)).then((r) => r.data);
export const adminUsers = (key, params) => api.get("/admin/users", { ...adminHdrs(key), params }).then((r) => r.data);
export const adminRefunds = (key, status) => api.get("/admin/refunds", { ...adminHdrs(key), params: status ? { status } : {} }).then((r) => r.data);
export const adminRefundApprove = (id, key, body) => api.post(`/admin/refunds/${id}/approve`, body, adminHdrs(key)).then((r) => r.data);
export const adminRefundDeny = (id, key, body) => api.post(`/admin/refunds/${id}/deny`, body, adminHdrs(key)).then((r) => r.data);
export const adminGrantFreeMonth = (user_id, key, body) => api.post(`/admin/users/${user_id}/grant-free-month`, body, adminHdrs(key)).then((r) => r.data);
export const adminDigestPreview = (key) => api.post("/cron/admin-digest/preview", {}, adminHdrs(key)).then((r) => r.data);

// Subjects
export const listSubjects = () => api.get("/subjects").then((r) => r.data);
export const createSubject = (data) => api.post("/subjects", data).then((r) => r.data);
export const getSubject = (id) => api.get(`/subjects/${id}`).then((r) => r.data);
export const updateSubject = (id, data) =>
  api.patch(`/subjects/${id}`, data).then((r) => r.data);
export const deleteSubject = (id) => api.delete(`/subjects/${id}`).then((r) => r.data);

// Notes
export const listNotes = (sid) =>
  api.get(`/subjects/${sid}/notes`).then((r) => r.data);
export const getNote = (id) => api.get(`/notes/${id}`).then((r) => r.data);
export const addTextNote = (sid, data) =>
  api.post(`/subjects/${sid}/notes/text`, data).then((r) => r.data);
export const uploadNote = (sid, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api
    .post(`/subjects/${sid}/notes/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};
export const deleteNote = (id) => api.delete(`/notes/${id}`).then((r) => r.data);

// Revision Books
export const genRevisionBook = (sid) =>
  api.post(`/subjects/${sid}/revision-books/generate`).then((r) => r.data);
export const listBooks = (sid) =>
  api.get(`/subjects/${sid}/revision-books`).then((r) => r.data);
export const getBook = (id) => api.get(`/revision-books/${id}`).then((r) => r.data);
export const updateBook = (id, data) =>
  api.patch(`/revision-books/${id}`, data).then((r) => r.data);
export const deleteBook = (id) =>
  api.delete(`/revision-books/${id}`).then((r) => r.data);
export const bookPdfUrl = (id) => `${API}/revision-books/${id}/pdf`;

// Flashcards
export const genFlashcards = (sid, count) =>
  api.post(`/subjects/${sid}/flashcards/generate`, { count }).then((r) => r.data);
export const listFlashcards = (sid) =>
  api.get(`/subjects/${sid}/flashcards`).then((r) => r.data);
export const reviewFlashcard = (id, correct) =>
  api.post(`/flashcards/${id}/review`, { correct }).then((r) => r.data);
export const deleteFlashcard = (id) =>
  api.delete(`/flashcards/${id}`).then((r) => r.data);

// Podcasts
export const genPodcast = (sid, style, tone) =>
  api.post(`/subjects/${sid}/podcasts/generate`, { style, tone }).then((r) => r.data);
export const listPodcasts = (sid) =>
  api.get(`/subjects/${sid}/podcasts`).then((r) => r.data);
export const podcastAudioUrl = (id) => `${API}/podcasts/${id}/audio`;
export const deletePodcast = (id) =>
  api.delete(`/podcasts/${id}`).then((r) => r.data);

// Chat
export const getChatHistory = (sid) =>
  api.get(`/subjects/${sid}/chat`).then((r) => r.data);
export const sendChat = (sid, message) =>
  api.post(`/subjects/${sid}/chat`, { message }).then((r) => r.data);
